// Three-component scoring engine: Base (0-65) + Near-Miss Credit (0-10) + Street Bonus (0-25)

import type { HandCategory } from './matrix.js';

export type Street = 'preflop' | 'flop' | 'turn' | 'river';

export interface EliminationEntry {
  handLabel: HandCategory;
  street: Street;
  timestamp: number;
}

export interface ScoreBreakdown {
  hit: boolean;
  finalRangeWidth: number;
  baseScore: number;          // 0-65: awarded on hit only
  nearMissCredit: number;     // 0-10: awarded on near-miss only (hit = false)
  nearMissDistance: number | null; // 1-3: D per spec (1=last elim, 2=second-to-last, 3=third-to-last)
  streetBonus: number;        // 0-25: awarded on isolation + hit
  isolationStreet: Street | null;
  totalScore: number;         // 0-100
}

const STREET_BONUS: Record<Street, number> = {
  preflop: 25,
  flop: 20,
  turn: 12,
  river: 5,
};

// D=1 (last elimination) = 10, D=2 = 6, D=3 = 3
const NEAR_MISS_CREDIT_BY_D: Record<number, number> = {
  1: 10,
  2: 6,
  3: 3,
};

const N = 169;

export function computeScore(params: {
  actualHand: HandCategory;
  finalRange: HandCategory[];           // highlighted cells at showdown
  eliminationLog: EliminationEntry[];   // append-only, in order
  isolationStreet: Street | null;
}): ScoreBreakdown {
  const { actualHand, finalRange, eliminationLog, isolationStreet } = params;

  const hit = finalRange.includes(actualHand);
  const W = finalRange.length;

  // Base score (0-65), only on hit
  let baseScore = 0;
  if (hit && W > 0) {
    baseScore = W === 1 ? 65 : 65 * (1 - (W - 1) / (N - 1));
  }

  // Street bonus (0-25), only on hit + isolation
  let streetBonus = 0;
  if (hit && isolationStreet !== null) {
    streetBonus = STREET_BONUS[isolationStreet];
  }

  // Near-miss credit (0-10), only on miss
  let nearMissCredit = 0;
  let nearMissDistance: number | null = null;

  if (!hit) {
    // Find when the actual hand was eliminated
    const actualHandEliminationIdx = eliminationLog.findIndex(e => e.handLabel === actualHand);
    if (actualHandEliminationIdx !== -1) {
      // D = number of eliminations after the actual hand was removed (0-indexed) + 1
      // D=1 means it was the LAST thing eliminated (0 hands after it)
      const handsEliminatedAfter = eliminationLog.length - 1 - actualHandEliminationIdx;
      const D = handsEliminatedAfter + 1;
      if (D <= 3) {
        nearMissDistance = D;
        nearMissCredit = NEAR_MISS_CREDIT_BY_D[D] ?? 0;
      }
    }
  }

  const totalScore = Math.round((baseScore + nearMissCredit + streetBonus) * 10) / 10;

  return {
    hit,
    finalRangeWidth: W,
    baseScore: Math.round(baseScore * 10) / 10,
    nearMissCredit,
    nearMissDistance,
    streetBonus,
    isolationStreet,
    totalScore,
  };
}

/**
 * Detect isolation: returns the first street on which the user's highlighted range
 * contained exactly the actual hand and nothing else.
 */
export function detectIsolationStreet(
  actualHand: HandCategory,
  snapshots: Array<{ street: Street; highlightedCategories: HandCategory[] }>
): Street | null {
  for (const snap of snapshots) {
    if (snap.highlightedCategories.length === 1 && snap.highlightedCategories[0] === actualHand) {
      return snap.street;
    }
  }
  return null;
}
